<template>
    <div v-if="loading" class="lg:tw-relative">
        <Heading :size="2" title="Lade..." />
        <p class="tw-text-gray-700">
            Deine Reisedaten werden geladen...
        </p>
    </div>
    <div v-else-if="tripData" class="lg:tw-relative">
        <Heading :size="2" :title="headline" />
        <div class="tw-mt-4 tw-flex tw-flex-col">
            <p class="tw-text-gray-700">
                Hallo {{ tripData?.traveller?.user?.firstName || '' }}, fast geschafft!
            </p>
            <p class="tw-text-gray-700">
                Vielen Dank für Deine Anfrage bei WeDesignTrips. Unsere Erfahrung zeigt, dass ein paar Details unseren lokalen Experten bei der Reiseplanung enorm helfen und wirklich einen Unterschied machen.
            </p>
            <p class="tw-text-gray-700 tw-font-medium">
                Bitte ergänze Deine Reisewünsche. Wir können Deine Anfrage sonst leider nicht an einen unserer lokalen Experten weiterleiten.
            </p>
        </div>
        <travel-message-input 
            :country-name="countryName"
            :country="country"
            inquiry-type="follow-up-inquiry"
            v-model="followUpMessage"
        />
        <custom-button
            @click="routeChange()"
            :disabled="!followUpMessage || !followUpMessage.trim() || followUpMessage.length < longMessageThreshold"
        >
            Absenden &rarr;
        </custom-button>
        <testimonials @mounted="initTrustpilot" />
    </div>
    <div v-else-if="!loading && !tripData">
        <Heading :size="2" title="Ungültiger Link" />
        <p>
            Der Link, den Du verwendet hast, ist ungültig. Bitte kontaktiere uns unter <a href="mailto:info@wedesigntrips.com">info@wedesigntrips.com</a> falls der Fehler weiterhin besteht.
        </p>
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import { LONG_MESSAGE_THRESHOLD } from '../components/mixin/acquisitionConstants.js';
import CustomButton from "../components/baseComponents/CustomButton.vue";
import TravelMessageInput from "../components/travelDataComponents/TravelMessageInput.vue";
import Testimonials from "../components/travelDataComponents/Testimonials.vue";
import Heading from "../components/baseComponents/Heading.vue";

export default {
    name: 'UpdateMessage',
    components: {
        Testimonials,
        CustomButton,
        TravelMessageInput,
        Heading
    },
    data() {
        return {
            followUpMessage: '',
            tripData: null,
            loading: true
        };
    },
    created() {
        if (this.$route.params.id) {
            this.loadTripData(this.$route.params.id);
        } else {
            console.error('No trip ID found in route params');
        }
        
        // Track follow-up message page visit
        this.$gtm.trackEvent({
            event: 'followUpMessagePage',
            travelType: window.location.pathname.includes('reise-planen') ? 'country' : 'tour',
            country: this.country?.mainCountry.staticCountry.shortName || this.tour.mainCountry.staticCountry.shortName
        });
    },
    mounted() {
        // Initialize TrustPilot widget
        this.initTrustpilot();
    },
    computed: {
        ...mapGetters({
            travelData: 'tour/travelData',
            tour: 'tour/route',
            country: 'tour/country',
        }),
        countryName() {
            return this.country?.title || this.tour.mainCountry.staticCountry.shortName;
        },
        headline() {
            return `Bitte ergänze Deine Reisewünsche`;
        },
        longMessageThreshold() {
            return LONG_MESSAGE_THRESHOLD;
        }
    },
    methods: {
        ...mapActions({
            submitFollowUpMessage: 'tour/submitFollowUpMessage',
            updateMessage: 'tour/updateMessage',
        }),
        // Load trip data directly via API
        async loadTripData(tripId) {
            this.loading = true;
            try {
                // Determine the correct API endpoint based on user type
                // This logic is similar to what the MessengerBundle does
                
                const response = await fetch('/api/traveller-inquiry/'+tripId);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();

                // Store the trip data in the component's data
                this.tripData = data[0] || data;
                // Prefill the textarea with the inquiry's initial message if available
                if (this.tripData && this.tripData.initialMessage) {
                    this.followUpMessage = this.tripData.initialMessage;
                }
            } catch (error) {
                console.error('Error loading trip data:', error);
                this.tripData = null;
            } finally {
                this.loading = false;
            }
        },
        // Initialize TrustPilot widget with Firefox private browsing detection
        initTrustpilot() {
            try {
                if (window.Trustpilot) {
                    window.Trustpilot.loadFromElement(document.querySelector('.trustpilot-widget'));
                } else {
                    // Check if we might be in Firefox private browsing
                    const isPrivateMode = !window.indexedDB;
                    if (isPrivateMode) {
                        console.warn('TrustPilot widget may be blocked by Firefox private browsing mode');
                    }
                }
            } catch (error) {
                console.error('Error initializing TrustPilot widget:', error);
            }
        },
        async routeChange() {
            try {
                // Track the follow-up message submission
                this.$gtm.trackEvent({
                    event: 'followUpMessageSubmitted',
                    messageLength: this.followUpMessage.length,
                    country: this.country ? this.country.mainCountry.staticCountry.shortName : this.tour.mainCountry.staticCountry.shortName
                });

                // Submit the updated message using Vuex action
                const result = await this.updateMessage({
                    inquiryId: this.$route.params.id,
                    message: this.followUpMessage
                });

                console.log('Update message result:', result);

                // Check if message was successfully updated using the result
                if (result && result.messageUpdated) {
                    this.$store.commit('tour/setTravelData', {
                        user: this.tripData.traveller.user,
                        message: this.followUpMessage
                    });
                    // Navigate to success page
                    this.$router.push({ name: 'success' });
                } else {
                    // Handle case where message wasn't updated (e.g., too short)
                    alert('Deine Nachricht muss mindestens 300 Zeichen lang sein, um aktualisiert zu werden.');
                }

            } catch (error) {
                console.error('Follow-up message submission error:', error);
                
                // Provide more specific error messages
                let errorMessage = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
                
                if (error.message) {
                    if (error.message.includes('HTML instead of JSON')) {
                        errorMessage = 'Serverfehler: Bitte überprüfe deine Anmeldung und versuche es erneut.';
                    } else if (error.message.includes('Network Error')) {
                        errorMessage = 'Verbindungsfehler: Bitte überprüfe deine Internetverbindung.';
                    } else if (error.response && error.response.status === 401) {
                        errorMessage = 'Nicht autorisiert: Bitte melde dich erneut an.';
                    } else if (error.response && error.response.status === 404) {
                        errorMessage = 'Anfrage nicht gefunden.';
                    }
                }
                
                alert(errorMessage);
            }
        }
    }
}
</script>

 